/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ThaiDatePipe } from '@alfresco/aca-shared';

/**
 * Document-list cell that renders a date the way the rest of the product does:
 * Thai month names and a Buddhist Era year.
 *
 * ADF's own `date` column formats through Angular's `DatePipe`, which under the
 * `th` locale gives Thai months but a Common Era year — so the same document
 * would read 2026 here and 2569 on เอกสารทั้งหมด. Its stock preset also asks for
 * `timeAgo`, which renders "6 days ago" in English and is truncated by the
 * column width.
 *
 * Registered as `app.columns.thai-date`; the cell receives the row and its
 * column through `context`, so one component serves any date column.
 */
@Component({
  selector: 'app-thai-date-column',
  standalone: true,
  imports: [CommonModule, ThaiDatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `{{ (value | acaDate: 'd MMM y HH:mm') || '—' }}`,
  host: { class: 'app-thai-date-column' }
})
export class ThaiDateColumnComponent {
  value: string | null = null;

  @Input()
  set context(value: any) {
    const entry = value?.row?.node?.entry;
    const key: string = value?.col?.key ?? 'modifiedAt';
    this.value = key.split('.').reduce((node: any, part: string) => node?.[part], entry) ?? null;
  }
}
